import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import type { Snapshot } from '@platform/contracts/x';
import type {SnapshotPatch} from './revisionPatch';
import {applyXPatch,applyXMetrics,unpackX,type XSnapshot} from './xTransport';
export const money = (value: number, currency = 'TRY') => new Intl.NumberFormat('tr-TR', { style: 'currency', currency, maximumFractionDigits: value % 100 === 0 ? 0 : 2 }).format(value / 100);
export const currencySymbol = (currency: string) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).formatToParts(0).find(part => part.type === 'currency')?.value ?? currency;
// Anchor server time when data arrives, not when a clock happens to mount.
// Weak keys retain no history; unchanged timestamps in delta branches keep
// their original offset, including an upcoming draw that becomes current.
const clockOffsets=new WeakMap<Snapshot,number>();
function rememberClock(next:Snapshot,previous:Snapshot|null,receivedAt=Date.now()){
 const prior=previous?.drawId===next.drawId?previous:previous?.upcoming?.drawId===next.drawId?previous.upcoming:null;
 const offset=prior?.serverTimeUtc===next.serverTimeUtc?clockOffsets.get(prior):undefined;
 if(!clockOffsets.has(next))clockOffsets.set(next,offset??Date.parse(next.serverTimeUtc)-receivedAt);
 if(next.upcoming)rememberClock(next.upcoming,previous,receivedAt);
}
// Snapshot history is not a stream of new notifications. A synchronization
// boundary identifies all awards already known when live observation resumes.
type StreamBaseline = {generation:number;ready:boolean;drawId:string;awardIds:string[]};
// drawId/awardIds describe the synchronization baseline, not the current round.
// Keep that history stable for notifications; live effects follow currentDrawId.
export type AwardStreamBoundary = StreamBaseline & {currentDrawId:string};
export async function command(path: string, body: unknown = {}, studio = false): Promise<Snapshot> {
  const response = await fetch('/api/' + path, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(studio ? { 'X-Rehearsal-Control': 'local-studio' } : {}) }, body: JSON.stringify(body) });
  if (!response.ok) { const error = await response.json().catch(() => ({})); throw new Error(error.message || 'İşlem tamamlanamadı. Bağlantınızı kontrol edin.'); }
  return response.json() as Promise<Snapshot>;
}
export function useDraw() {
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [previousDraw,setPreviousDraw]=useState<Snapshot|null>(null);
  const [connected, setConnected] = useState(false);const [error, setError] = useState('');
  const [boundary,setBoundary]=useState<StreamBaseline>({generation:0,ready:false,drawId:'',awardIds:[]});
  const awardStream=useMemo<AwardStreamBoundary>(()=>({...boundary,currentDrawId:snapshot?.drawId??''}),[boundary,snapshot?.drawId]);
  const generation=useRef(0);
  const current = useRef<Snapshot | null>(null);
  const acceptSnapshot=useCallback((next:Snapshot)=>{
    const previous=current.current;
    // ADR-004: revision is monotonic across local rounds; an old fetch must not restore an earlier draw.
    if(previous&&previous.revision>next.revision)return;
    rememberClock(next,previous);
    // Preserve the final update even if React batches completion and promotion
    // into one render. This is a single snapshot, not client-side draw history.
    if(previous&&previous.drawId!==next.drawId)setPreviousDraw(previous);
    current.current=next;setSnapshot(next);
  },[]);
  useEffect(() => {
    let mounted = true;let retry:ReturnType<typeof setTimeout>|undefined;
    const connection = new HubConnectionBuilder().withUrl('/hubs/draw?updates=x-v3').withAutomaticReconnect([0, 1000, 3000, 5000]).configureLogging(LogLevel.Error).build();
    connection.on('sessionExpired',()=>{window.dispatchEvent(new Event('player:session-expired'));});
    connection.on('purchaseReceipt',detail=>{if(mounted)window.dispatchEvent(new CustomEvent('tombala:purchase-receipt',{detail}));});
    let baselinePending=true;
    let xCurrent:XSnapshot|null=null;
    const initial=(next:Snapshot)=>{if(!mounted)return;setError('');acceptSnapshot(next);if(baselinePending){baselinePending=false;setBoundary({generation:++generation.current,ready:true,drawId:next.drawId,awardIds:next.awards.map(a=>a.id)});}};

    connection.on('xSnapshot',(packed:XSnapshot)=>{const next=unpackX(packed);if(next){xCurrent=packed;initial(next);}else void refresh();});
    connection.on('uiChanged',detail=>{if(mounted)window.dispatchEvent(new CustomEvent('tombala:ui-changed',{detail}));});
    const invalidate=()=>{
      const next=++generation.current;
      if(mounted)setBoundary({generation:next,ready:false,drawId:'',awardIds:[]});
      return next;
    };
    const refresh=async()=>{
      const boundary=invalidate();
      try{
        const r=await fetch('/api/state?wire=x3',{signal:AbortSignal.timeout(connection.serverTimeoutInMilliseconds)});
        if(r.status===401){if(mounted)window.dispatchEvent(new Event('player:session-expired'));return;}
        if(!r.ok)throw new Error('Snapshot unavailable');
        const data=await r.json() as XSnapshot;
        const packed=data.v===5?data:null;
        const next=packed?unpackX(packed):null;
        if(!next)throw new Error('Snapshot incompatible');
        if(!mounted||boundary!==generation.current)return;
        setError('');
        if(packed&&(!xCurrent||packed.state.revision>=xCurrent.state.revision))xCurrent=packed;
        acceptSnapshot(next);
        // A hub update can beat the HTTP response. Baseline the latest accepted
        // revision, never the older response or a previous connection's ledger.
        const baseline=current.current;
        if(baseline&&connection.state===HubConnectionState.Connected&&!document.hidden){
          baselinePending=false;
          setBoundary({generation:boundary,ready:true,drawId:baseline.drawId,awardIds:baseline.awards.map(a=>a.id)});
        }
      }catch{if(mounted&&!current.current)setError('loading.failed');}
    };
    let recovering=false;
    connection.on('xPatch',(patch:SnapshotPatch)=>{
      if(!mounted||xCurrent&&patch.r<=xCurrent.state.revision)return;
      const packed=applyXPatch(xCurrent,patch),next=packed?unpackX(packed):null;
      if(packed&&next){xCurrent=packed;acceptSnapshot(next);return;}
      if(!recovering){recovering=true;void refresh().finally(()=>{recovering=false;});}
    });
    connection.on('xMetrics',(patch:SnapshotPatch)=>{
      if(!mounted||xCurrent&&patch.r<xCurrent.state.revision)return;
      const packed=applyXMetrics(xCurrent,patch),next=packed?unpackX(packed):null;
      if(packed&&next){xCurrent=packed;acceptSnapshot(next);return;}
      if(!recovering){recovering=true;void refresh().finally(()=>{recovering=false;});}
    });
    const schedule=()=>{clearTimeout(retry);if(mounted)retry=setTimeout(()=>void start(),3000);};
    const started=()=>{
      setConnected(true);setError('');window.dispatchEvent(new Event('tombala:ui-changed'));
      // A successful transport handshake does not prove delivery of a game
      // baseline. Recover once per connection, only if no full state arrived.
      if(baselinePending)void refresh();
    };
    const start=async()=>{
      if(!mounted||connection.state!==HubConnectionState.Disconnected)return;
      baselinePending=true;
      try{await connection.start();if(mounted)started();else await connection.stop();}
      catch{if(mounted){setConnected(false);setError('connection.pending');schedule();}}
    };
    connection.onreconnecting(()=>{if(mounted){baselinePending=true;setConnected(false);invalidate();}});
    connection.onreconnected(()=>{if(mounted)started();});
    connection.onclose(()=>{if(mounted){setConnected(false);invalidate();schedule();}});
    void start();
    const onVisible=()=>{
      invalidate();
      if(document.visibilityState==='visible'){
        if(connection.state===HubConnectionState.Connected)void refresh();
        else if(connection.state===HubConnectionState.Disconnected)void start();
      }
    };
    document.addEventListener('visibilitychange',onVisible);
    return()=>{mounted=false;clearTimeout(retry);document.removeEventListener('visibilitychange',onVisible);void connection.stop();};
  },[acceptSnapshot]);
  return {snapshot,previousDraw,connected,error,acceptSnapshot,awardStream};
}
export function getServerTime(snapshot:Snapshot){
  if(!clockOffsets.has(snapshot))rememberClock(snapshot,null);
  return Date.now()+clockOffsets.get(snapshot)!;
}
// Presentation follows the schedule already announced by the server. Waiting
// for the durable SalesOpen -> Preparing packet loses the first seconds when
// persistence/publication is late. Transport state and financial receipts stay
// authoritative; this never manufactures Live, balls or purchase acceptance.
export function useScheduledDraw(snapshot:Snapshot|null){
  const [closedDraw,setClosedDraw]=useState<string|null>(null);
  const cutoff=Date.parse(snapshot?.salesCloseAtUtc??'');
  const countdownEnd=Date.parse(snapshot?.drawCountdownEndsAtUtc??'');
  const firstBall=Date.parse(snapshot?.drawStartsAtUtc??'');
  const scheduled=!!snapshot&&!snapshot.isUpcoming&&snapshot.status==='SalesOpen'&&
    Number.isFinite(cutoff)&&Number.isFinite(countdownEnd)&&Number.isFinite(firstBall)&&
    countdownEnd>=cutoff&&firstBall>=countdownEnd;
  const closed=scheduled&&(closedDraw===snapshot!.drawId||getServerTime(snapshot!)>=cutoff);
  useEffect(()=>{
    if(!snapshot||!scheduled)return;
    let timer:ReturnType<typeof setTimeout>;
    const update=()=>{
      clearTimeout(timer);
      const remaining=cutoff-getServerTime(snapshot);
      if(remaining<=0)setClosedDraw(snapshot.drawId);
      else timer=setTimeout(update,Math.min(remaining+1,2147483647));
    };
    update();
    document.addEventListener('visibilitychange',update);window.addEventListener('focus',update);
    return()=>{clearTimeout(timer);document.removeEventListener('visibilitychange',update);window.removeEventListener('focus',update);};
  },[snapshot,scheduled,cutoff]);
  return useMemo(()=>{
    if(!snapshot||!closed)return snapshot;
    const view:Snapshot={...snapshot,status:'Preparing',tombala:{...snapshot.tombala,status:'Preparing'}};
    // A derived view must keep the receipt-time clock anchor, not remount at
    // the old server timestamp. This also keeps both clocks and sound in sync.
    rememberClock(view,snapshot);
    return view;
  },[snapshot,closed]);
}
export function useCountdown(snapshot: Snapshot, targetUtc = snapshot.salesCloseAtUtc) {
  if(!clockOffsets.has(snapshot))rememberClock(snapshot,null);
  const serverOffset=clockOffsets.get(snapshot)!;
  const secondsLeft=()=>Math.max(0,Math.ceil(((Date.parse(targetUtc??'')||0)-Date.now()-serverOffset)/1000));
  const [remaining, setRemaining] = useState(secondsLeft);
  useEffect(() => {
    let timer:ReturnType<typeof setTimeout>;
    // Follow the server deadline's second boundaries, including late arrivals.
    // A free-running polling interval can put each visual beat behind the audio.
    const update=()=>{
      const milliseconds=(Date.parse(targetUtc??'')||0)-Date.now()-serverOffset;
      setRemaining(Math.max(0,Math.ceil(milliseconds/1000)));
      if(milliseconds>0)timer=setTimeout(update,(milliseconds%1000||1000)+1);
    };
    update();return()=>clearTimeout(timer);
  }, [serverOffset, targetUtc]);
  return remaining;
}
